import {useContext, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'

import {useGetTrackingEvents} from '../../hooks/useGetTrackingEvents.js'
import {
  FDA_GENERAL_ERROR,
  FDA_USER_STOPPED_EXECUTION_ERROR,
  MAX_RETRIES,
  MODAL_CONTENT_IDS,
  RETRY_INTERVAL
} from '../config.js'
import {PanelFinancingCta} from '../cta/PanelFinancingCta.js'
import {PanelFinancingModal} from '../modal/PanelFinancingModal.js'

export const PanelFinancingCtaB2c = ({
  ad,
  financingData,
  isDisabled = false,
  selectedLoanData = {},
  loanCalculations = {}
}) => {
  const {domain, i18n} = useContext(Context)
  const [modalContentId, setModalContentId] = useState('')
  const stopExecution = useRef(false)

  const {fdaLoading, fdaError} = MODAL_CONTENT_IDS
  const {trackCTA, trackLoanAplicationFailed} = useGetTrackingEvents()

  const handleModalClose = () => {
    stopExecution.current = true
    setModalContentId('')
  }

  const manageGetLoanUrlB2C = response => {
    switch (response) {
      case FDA_USER_STOPPED_EXECUTION_ERROR:
        stopExecution.current = false
        break
      case FDA_GENERAL_ERROR:
        trackLoanAplicationFailed(ad)
        setModalContentId(fdaError)
        break
      default:
        setModalContentId('')
        window && window.open(response)
        break
    }
  }

  const getLoanUrlB2C = async ({loanId}) => {
    const [error, loanUrl] = await domain.get('get_loan_url_b2c_transactions_use_case').execute({loanId})
    if (error) manageGetLoanUrlB2C(FDA_GENERAL_ERROR)
    else return loanUrl
  }

  const createLoanIdB2C = async ({adId, entry, fee, terms, withLifeInsurance, withGapInsurance}) => {
    const [error, loanId] = await domain.get('create_loan_id_b2c_transactions_use_case').execute({
      adId,
      entry,
      fee,
      terms,
      withLifeInsurance,
      withGapInsurance
    })
    if (error) manageGetLoanUrlB2C(FDA_GENERAL_ERROR)
    else return loanId
  }

  // recursive retrieve loan  url
  const wait = time => new Promise(resolve => setTimeout(resolve, time))

  const recursiveRetrieveLoanUrl = async ({loanId, loanUrl = '', currentRetry = 0}) => {
    if (stopExecution.current) return FDA_USER_STOPPED_EXECUTION_ERROR

    if (!stopExecution.current && !loanUrl && currentRetry < MAX_RETRIES) {
      await wait(RETRY_INTERVAL * currentRetry)
      const response = await getLoanUrlB2C({loanId})
      return recursiveRetrieveLoanUrl({
        loanId,
        loanUrl: response,
        currentRetry: currentRetry + 1
      })
    }
    if (!loanUrl && currentRetry === MAX_RETRIES) return FDA_GENERAL_ERROR

    return loanUrl
  }

  const handleOnClickB2C = async () => {
    setModalContentId(fdaLoading)

    trackCTA(ad, loanCalculations)

    const loanId = await createLoanIdB2C({
      adId: ad.id,
      entry: Number(selectedLoanData?.entryValue) || 0,
      fee: selectedLoanData?.fee,
      terms: selectedLoanData?.selectedTerms,
      withLifeInsurance: selectedLoanData?.fareHasInsurance,
      withGapInsurance: loanCalculations?.insuranceCoverages?.gap
    })
    const loanUrl = await recursiveRetrieveLoanUrl({loanId})
    manageGetLoanUrlB2C(loanUrl)
  }

  return (
    <>
      <PanelFinancingCta
        buttonText={i18n.t(`COMMON.CONTINUE`)}
        handleOnClick={handleOnClickB2C}
        isDisabled={isDisabled}
        isProfessional
      />
      {modalContentId !== '' && (
        <PanelFinancingModal contentId={modalContentId} financingData={financingData} onModalClose={handleModalClose} />
      )}
    </>
  )
}

PanelFinancingCtaB2c.propTypes = {
  ad: PropTypes.object,
  financingData: PropTypes.object,
  isDisabled: PropTypes.bool,
  selectedLoanData: PropTypes.object,
  loanCalculations: PropTypes.object
}
